import React from 'react'
import { useNavigate } from 'react-router-dom'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { ROUTES } from 'shared/config/routes'
import { fetchStudentApplications } from 'api/applications'
import {
	formatFeedbackCardTime,
	formatFeedbackCardTimeShort,
} from '../../utils/feedbackMapping'
import {
	dashboardPriorityBackState,
	getUrgencyMeta,
	navigateToStudentProfileFromPriority,
} from '../../utils/priorityCardUi'
import { ReactComponent as DockIcon } from 'assets/icons/dock.svg'
import { ReactComponent as CityIcon } from 'assets/icons/city.svg'
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg'
import { ReactComponent as MessageIcon } from 'assets/icons/message.svg'
import { ReactComponent as ExternalIcon } from 'assets/icons/open-external.svg'
import s from './FeedbackCard.module.scss'

const FeedbackCard = ({ item, onOpen, sourceTab = null }) => {
	const navigate = useNavigate()
	const previewText = String(item?.previewMessage?.text || '')
		.replace(/\s+/g, ' ')
		.trim()
	const timeLabel =
		formatFeedbackCardTime(item?.previewMessage?.createdAt) || 'Recently'
	const timeLabelShort =
		formatFeedbackCardTimeShort(item?.previewMessage?.createdAt) || timeLabel
	const unreadCount = Number(item?.unreadCount) || 0
	const localSeen = Boolean(item?._localSeen)
	const showNewBadge = unreadCount > 0 && !localSeen
	const urgency = getUrgencyMeta(item)
	const scope = String(item?.scope || '').toLowerCase()
	const isCommonApp = scope === 'common_app'
	const universityName = String(item?.universityName || '').trim()
	const programName = String(item?.programName || '').trim()
	const metaLine = isCommonApp
		? 'Common App'
		: [universityName, programName].filter(Boolean).join(' · ') ||
			(universityName.toLowerCase() === 'common app' ? 'Common App' : '')

	const appNavOptions = {
		state: dashboardPriorityBackState(sourceTab),
	}

	const handleOpenApplication = async e => {
		e.stopPropagation()
		const appId = item?.applicationId || item?.applicationTargetId
		if (appId) {
			navigate(
				`${ROUTES.APPLICATIONS}/${encodeURIComponent(appId)}`,
				appNavOptions,
			)
			return
		}
		if (!item?.studentId) return
		try {
			const res = await fetchStudentApplications(item.studentId)
			const apps = Array.isArray(res?.applications)
				? res.applications
				: Array.isArray(res?.data?.applications)
					? res.data.applications
					: []
			const firstId = apps[0]?.id || apps[0]?.applicationId || null
			if (firstId) {
				navigate(
					`${ROUTES.APPLICATIONS}/${encodeURIComponent(firstId)}`,
					appNavOptions,
				)
				return
			}
		} catch (error) {
			console.error(error)
		}
		navigateToStudentProfileFromPriority(
			navigate,
			item.studentId,
			sourceTab,
		)
	}

	const handleOpenStudent = e => {
		e.stopPropagation()
		navigateToStudentProfileFromPriority(
			navigate,
			item?.studentId,
			sourceTab,
		)
	}

	const handleOpenFeedback = e => {
		e.stopPropagation()
		onOpen?.()
	}

	const studentName = item?.studentName || 'Student'

	return (
		<article className={s.card}>
			<div className={s.header}>
				<div className={s.headerLead}>
					<div className={s.avatar}>
						<InitialAvatar
							size={40}
							name={studentName}
							studentId={item?.studentId}
							photoUrl={item?.studentAvatarUrl}
							avatarSource={
								item?.studentAvatarUrl ? 'photo' : undefined
							}
							onClick={handleOpenStudent}
						/>
					</div>
					<div className={s.identity}>
						<button
							type="button"
							className={s.name}
							onClick={handleOpenStudent}
						>
							{studentName}
						</button>
						{metaLine ? (
							<span className={s.meta}>
								{isCommonApp || metaLine === 'Common App' ? (
									<DockIcon />
								) : (
									<CityIcon />
								)}
								<span>{metaLine}</span>
							</span>
						) : null}
					</div>
				</div>
				<div className={s.badgeStack}>
					{urgency?.label ? (
						<span className={s.urgent}>{urgency.label}</span>
					) : null}
					{showNewBadge ? (
						<span className={s.status}>{unreadCount} new</span>
					) : null}
				</div>
			</div>

			<div className={s.body}>
				<div className={s.metaRow}>
					<span className={s.sectionLabel}>
						<MessageIcon />
						<span>Feedback Reply</span>
					</span>
					<span className={s.time}>
						<ClockIcon />
						<span className={s.timeFull}>{timeLabel}</span>
						<span className={s.timeShort}>{timeLabelShort}</span>
					</span>
				</div>

				<p className={s.preview}>
					{previewText || 'Student replied to your feedback.'}
				</p>

				<div className={s.essayBox}>
					<span className={s.essayLabel}>Essay title</span>
					<span className={s.essayTitle}>{item?.title || 'Essay'}</span>
				</div>
			</div>

			<div className={s.footer}>
				<button type="button" className={s.openBtn} onClick={handleOpenApplication}>
					<ExternalIcon />
					Open Application
				</button>
				<button
					type="button"
					className={s.reviewBtn}
					onClick={handleOpenFeedback}
				>
					<MessageIcon />
					Open Feedback
				</button>
			</div>
		</article>
	)
}

export default FeedbackCard
